import { registerBrick } from 'olympe';
import { ReactBrick, executeLambda } from '@olympeio/core';
import { combineLatest } from 'rxjs';
import React, { useCallback, useMemo, useState, useRef } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Stack,
    Typography,
    Tooltip,
    Paper,
    Divider,
    Tabs,
    Tab,
    Chip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export default class APIMonitor extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('API Name', false),
            $.observe('Input Parameters', false),
            $.observe('API Caller', false)
        ]);
    }

    init($) {
        super.init($);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [appName, inputParameter, funcExecutor] = props.values;

            const [loading, setLoading] = useState(false);
            const [success, setSuccess] = useState(null); // null | boolean
            const [response, setResponse] = useState(null); // any
            const [dialogOpen, setDialogOpen] = useState(false);
            const [tab, setTab] = useState(0);

            const statusColor = useMemo(() => {
                if (success === true) return 'success';
                if (success === false) return 'error';
                return 'default';
            }, [success]);

            const pretty = useCallback((val) => {
                if (val === null || val === undefined) return '';
                if (typeof val === 'string') {
                    try { return JSON.stringify(JSON.parse(val), null, 2); } catch { return val; }
                }
                return JSON.stringify(val, null, 2);
            }, []);

            const callAPI = useCallback(async () => {
                setLoading(true);
                try {
                    const res = await executeLambda($, funcExecutor, [inputParameter], 400);

                    setSuccess(true);
                    setResponse(res ?? null);

                } catch (e) {
                    setSuccess(false);
                    setResponse({ error: String(e && e.message ? e.message : e) });
                } finally {
                    setLoading(false);
                }
            }, [$, inputParameter, funcExecutor]);

            const didSubscribeRef = useRef(false);
            if (!didSubscribeRef.current) {
                didSubscribeRef.current = true;
                $.observe('Call API').subscribe(() => {
                    callAPI();
                });
            }

            return (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="h6" fontWeight={600}>{appName || 'API Name'}</Typography>
                    </Stack>

                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        {/* Keep everything on one line but allow horizontal scroll when too narrow */}
                        <Stack direction="row" spacing={3} alignItems="center" sx={{ minWidth: 560, py: 0.5 }}>
                            <Button size="medium" variant="contained" onClick={callAPI} disabled={loading}>
                                {loading ? 'CALLING…' : 'CALL API'}
                            </Button>

                            <Stack direction="row" spacing={2} alignItems="center">
                                <Typography variant="subtitle2" color="text.secondary">Response status</Typography>
                                <Chip
                                    label={success === null ? 'Idle' : success ? 'Success' : 'Failed'}
                                    color={statusColor}
                                    variant={success === null ? 'outlined' : 'filled'}
                                    size="small"
                                />
                                <Tooltip title={success === null ? 'No data yet' : 'View details'}>
                                    <span>
                                        <IconButton
                                            aria-label="Show last response"
                                            color={success === null ? 'disabled' : 'primary'}
                                            onClick={() => {
                                                if (success !== null) {
                                                    setTab(0);
                                                    setDialogOpen(true);
                                                }
                                            }}
                                            disabled={success === null}
                                        >
                                            <InfoOutlinedIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </Box>

                    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                        <DialogTitle>API Details</DialogTitle>
                        <Divider />
                        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="response inputs tabs" variant="fullWidth">
                            <Tab label="Response" />
                            <Tab label="Inputs" />
                        </Tabs>
                        <DialogContent dividers sx={{ maxHeight: 480, overflow: 'auto', pt: 1 }}>
                            {tab === 0 && (
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
{pretty(response)}
                                </pre>
                            )}
                            {tab === 1 && (
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
{pretty(inputParameter)}
                                </pre>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDialogOpen(false)}>Close</Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            );
        };
    }
}

registerBrick('01999f4b11163e53a384', APIMonitor);
